import { Search, ShoppingCart, User, Utensils, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useOrders } from '../../hooks/useOrders';
import { useCustomers } from '@/hooks/useCustomers';
import { useCategories } from '@/hooks/useCategories';

interface GlobalSearchProps {
  onNavigate?: (page: string, id?: string) => void;
  onOrderClick?: (orderId: string) => void;
  onCustomerClick?: (customerId: string) => void;
  onCategoryClick?: (categoryId: string) => void;
}

interface SearchResult {
  id: string;
  type: 'order' | 'customer' | 'member' | 'menuItem' | 'category';
  title: string;
  subtitle: string;
  icon: 'order' | 'customer' | 'menu';
}

export function GlobalSearch({ /*onNavigate,*/ onOrderClick, onCustomerClick, onCategoryClick }: GlobalSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  //Get the orders from the hook to have the most up-to-date data
  const { orders: fetchedOrders } = useOrders();
  const orders = fetchedOrders || [];

  const { customers: fetchedCustomers } = useCustomers();
  const customers = fetchedCustomers || [];

  const { categories: fetchedCategories } = useCategories();
  const categories = fetchedCategories || [];


  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search function
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const searchResults: SearchResult[] = [];

    // Search Orders
    orders.forEach((order) => {
      const matchesOrderId = order.orderNumber.toLowerCase().includes(query);
      const matchesCustomer = order.customer?.firstName?.toLowerCase().includes(query);
      const matchesLocation = order.customer?.lastName ? order.customer.lastName.toLowerCase().includes(query) : false;

      const customerName = [order.customer?.firstName,order.customer?.lastName].filter(Boolean).join(' ');
      const displayName = customerName || [order.paymentMethod, ' - ', order.paymentStatus].filter(Boolean).join(' ') ||'Walk-in';
      
      if (matchesOrderId || matchesCustomer || matchesLocation) {
        searchResults.push({
          id: order.id,
          type: 'order',
          title: `Order ${order.orderNumber}`,
          subtitle: `${displayName} - ${order.total}`,
          icon: 'order'
        });
      }
    });

    // Search Customers
    customers.forEach((customer) => {
      const matchesName = [customer.firstName, customer.lastName].some(name => name?.toLowerCase().includes(query));
      const matchesLocation = customer.address ? customer.address.toLowerCase().includes(query) : false;
      const matchesId = customer.id.toLowerCase().includes(query);
      
      if (matchesName || matchesLocation || matchesId) {
        searchResults.push({
          id: customer.id,
          type: 'customer',
          title: customer.firstName + ' ' + customer.lastName,
          subtitle: `${customer.firstName + ' ' + customer.lastName} - Total: ${customer.totalSpent}`,
          icon: 'customer'
        });
      }
    });

    // Search Categories
    categories.forEach((category) => {
      const matchesName = category.name.toLowerCase().includes(query);
      const matchesDescription = category.description ? category.description.toLowerCase().includes(query) : false;
      
      if (matchesName || matchesDescription) {
        searchResults.push({
          id: category.id,
          type: 'category',
          title: category.name,
          subtitle: `Category - ${category.description} items`,
          icon: 'menu'
        });
      }
    });

    // Limit to 10 results
    setResults(searchResults.slice(0, 10));
    setShowResults(true);
  }, [searchQuery]);

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'order':
        onOrderClick?.(result.id);
        break;
      case 'customer':
        onCustomerClick?.(result.id);
        break;
      case 'member':
        onCustomerClick?.(result.id);
        break;
      case 'category':
        onCategoryClick?.(result.id);
        break;
    }
    setSearchQuery('');
    setShowResults(false);
  };

  const getResultIcon = (icon: string) => {
    switch (icon) {
      case 'order':
        return <ShoppingCart className="w-4 h-4" />;
      case 'customer':
        return <User className="w-4 h-4" />;
      case 'menu':
        return <Utensils className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  const getResultIconBg = (icon: string) => {
    switch (icon) {
      case 'order':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'customer':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'menu':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'order':
        return 'Order';
      case 'customer':
        return 'Customer';
      case 'member':
        return 'Member';
      case 'menuItem':
        return 'Menu Item';
      case 'category':
        return 'Category';
      default:
        return '';
    }
  };

  return (
    <div className="relative w-full max-w-sm lg:max-w-md" ref={searchRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
        placeholder="Search orders, customers, menu..."
        className="w-full pl-10 pr-10 py-2 text-sm bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border-none rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      {searchQuery && (
        <button
          onClick={() => {
            setSearchQuery('');
            setShowResults(false);
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
        >
          <X className="w-4 h-4 text-gray-400 dark:text-gray-500" />
        </button>
      )}

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl rounded-xl max-h-96 overflow-y-auto z-50">
          {results.length === 0 ? (
            <div className="p-8 text-center">
              <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No results found for "{searchQuery}"
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Try searching for orders, customers, or menu items
              </p>
            </div>
          ) : (
            <div>
              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Found {results.length} result{results.length !== 1 ? 's' : ''}
                </p>
              </div>
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}-${index}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full p-3 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors flex items-start gap-3 text-left border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <div className={`p-2 rounded-lg flex-shrink-0 ${getResultIconBg(result.icon)}`}>
                    {getResultIcon(result.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {result.title}
                      </p>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full flex-shrink-0">
                        {getTypeLabel(result.type)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {result.subtitle}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}